import {
	NgFor,
	NgIf,
	NgSwitch,
	NgSwitchCase,
	NgSwitchDefault
} from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Optional,
	Output,
	ViewChild
} from '@angular/core';
import {
	CdkConnectedOverlay,
	ConnectedPosition,
	OverlayModule
} from '@angular/cdk/overlay';
import { TypographyComponent } from 'components';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { ReportFigureFlagsService } from '../../../../services/report-figure-flags.service';
import {
	ReportInlineTextField,
	textBlockField
} from '../../../../services/report-inline-edit.service';
import {
	ReportFigureFlag,
	figurePlace
} from '../../../../services/report-quoted-figures';
import { ReportFigureFlagsComponent } from '../report-figure-flags/report-figure-flags.component';
import {
	REPORT_TEXT_INSERTS,
	ReportTextBlock,
	ReportTextInsert,
	ReportTextInsertId,
	insertKind
} from '../../../../services/report-text-blocks';
import { ReportInlineTextComponent } from '../report-inline-text/report-inline-text.component';
import { ReportRichTextComponent } from '../report-rich-text/report-rich-text.component';

/** What the slash menu opens on: a "/" typed into an empty block, and what
 *  follows it up to the first space. */
const SLASH_QUERY = /^\/([^\s/]*)$/;

/**
 * One text block, drawn as its kind — a heading, a paragraph, a callout or a
 * divider — on every surface: the editor, the PDF and the share page.
 *
 * In the editor its words are typed where they stand, through the same fields
 * the sections use. A "/" typed into an empty paragraph or callout opens the
 * slash menu: another kind of block, a list, a chart from the library or a
 * picture from Ana. What the block can do on its own — clear the "/", start a
 * list — it does; everything else it hands up as `inserted`, having let go of
 * its field first, so that the "/" is never saved as the block's text.
 */
@Component({
	standalone: true,
	selector: 'app-report-text-block',
	templateUrl: './report-text-block.component.html',
	styleUrls: ['./report-text-block.component.scss'],
	imports: [
		NgFor,
		NgIf,
		NgSwitch,
		NgSwitchCase,
		NgSwitchDefault,
		OverlayModule,
		TypographyComponent,
		ReportFigureFlagsComponent,
		ReportInlineTextComponent,
		ReportRichTextComponent
	]
})
export class ReportTextBlockComponent implements OnInit, OnChanges, OnDestroy {
	@Input() set block(value: ReportTextBlock) {
		this.current = value;
		this.field = value ? textBlockField(value) : null;
	}
	get block(): ReportTextBlock {
		return this.current;
	}
	@Input() editorMode = false;
	/** The report's colour, for a callout's accent. */
	@Input() themeColor: string | null = null;

	/** Picked from the slash menu, for the list to carry out. */
	@Output() inserted = new EventEmitter<ReportTextInsertId>();

	@ViewChild(ReportRichTextComponent) private rich?: ReportRichTextComponent;
	@ViewChild(ReportInlineTextComponent)
	private heading?: ReportInlineTextComponent;
	@ViewChild(CdkConnectedOverlay) private slashOverlay?: CdkConnectedOverlay;

	field: ReportInlineTextField | null = null;
	/** The slash menu is open; see `setSlashOpen`. */
	slashOpen = false;
	slashOptions: ReportTextInsert[] = REPORT_TEXT_INSERTS;
	slashIndex = 0;

	readonly slashPositions: ConnectedPosition[] = [
		{
			originX: 'start',
			originY: 'bottom',
			overlayX: 'start',
			overlayY: 'top',
			offsetY: 6
		},
		{
			originX: 'start',
			originY: 'top',
			overlayX: 'start',
			overlayY: 'bottom',
			offsetY: -6
		}
	];

	/**
	 * Where a paragraph's or a callout's words live, for their flags; null
	 * outside the editor and for a heading, whose field flags its own.
	 */
	place: string | null = null;
	/** The numbers the live data has moved on from, marked in the words. */
	marks: ReadonlySet<string> = new Set();

	private current!: ReportTextBlock;
	private readonly place$ = new BehaviorSubject<string | null>(null);
	private readonly destroy$ = new Subject<void>();

	constructor(
		@Optional() private figureFlags: ReportFigureFlagsService | null
	) {}

	get id(): string {
		return this.current?.id;
	}

	ngOnInit(): void {
		this.place$
			.pipe(
				distinctUntilChanged(),
				switchMap(place =>
					this.figureFlags && place
						? this.figureFlags.flagsFor(place)
						: of<ReportFigureFlag[]>([])
				),
				takeUntil(this.destroy$)
			)
			.subscribe(flags => {
				this.marks = new Set(flags.map(flag => flag.quoted));
			});
	}

	ngOnChanges(): void {
		const kind = this.current?.kind;
		this.place =
			this.editorMode && kind && kind !== 'heading' && kind !== 'divider'
				? figurePlace({ kind: 'text_block', id: this.current.id })
				: null;
		this.place$.next(this.place);
	}

	ngOnDestroy(): void {
		document.removeEventListener('scroll', this.onScroll, true);
		this.destroy$.next();
		this.destroy$.complete();
	}

	get placeholder(): string {
		return this.current?.kind === 'callout'
			? 'A takeaway to set apart'
			: 'Type, or press / for a heading, a list, a chart…';
	}

	/** Open the block's field for typing — and start a list in it, for the
	 *  two lists the library and the slash menu offer. */
	focus(list?: 'bullets' | 'numbers'): void {
		if (this.current.kind === 'heading') {
			this.heading?.focusField();
			return;
		}
		this.rich?.focusField();
		if (list) {
			this.rich?.startList(list === 'numbers');
		}
	}

	onTyping(visible: string): void {
		const query = SLASH_QUERY.exec(visible.trim())?.[1];
		// Opened by a "/" in an empty block, and only there; once open it
		// follows the letters after it, and a space or no match closes it.
		if (!this.slashOpen && visible.trim() !== '/') {
			return;
		}
		if (query === undefined) {
			this.setSlashOpen(false);
			return;
		}
		const needle = query.toLowerCase();
		this.slashOptions = REPORT_TEXT_INSERTS.filter(option =>
			`${option.label} ${option.keywords}`
				.toLowerCase()
				.split(/\s+/)
				.some(word => word.startsWith(needle))
		);
		this.slashIndex = 0;
		this.setSlashOpen(this.slashOptions.length > 0);
	}

	/** The slash menu's keys, taken before the field acts on them. */
	onKey(event: KeyboardEvent): void {
		if (!this.slashOpen) {
			return;
		}
		const count = this.slashOptions.length;
		switch (event.key) {
			case 'ArrowDown':
				this.slashIndex = (this.slashIndex + 1) % count;
				break;
			case 'ArrowUp':
				this.slashIndex = (this.slashIndex - 1 + count) % count;
				break;
			case 'Enter':
			case 'Tab':
				this.pick(this.slashOptions[this.slashIndex]);
				break;
			case 'Escape':
				// Closes the menu and keeps the "/", as typed.
				this.setSlashOpen(false);
				break;
			default:
				return;
		}
		event.preventDefault();
	}

	/** The field let go of — clicked away, Esc, or a pick handed on. */
	closeSlashMenu(): void {
		if (this.slashOpen) {
			this.setSlashOpen(false);
		}
	}

	pick(option: ReportTextInsert): void {
		this.setSlashOpen(false);
		const kind = insertKind(option.id);
		const list =
			option.id === 'bullets' || option.id === 'numbers'
				? option.id
				: null;
		// What this block already is: the "/" goes and typing carries on.
		if (
			kind === this.current.kind ||
			(list && this.current.kind !== 'heading')
		) {
			this.rich?.clearTyping();
			if (list) {
				this.rich?.startList(list === 'numbers');
			}
			return;
		}
		this.rich?.cancel();
		this.inserted.emit(option.id);
	}

	/**
	 * The editor scrolls inside its own column, which the overlay's own
	 * strategy does not hear, so the menu is moved with the block while open.
	 */
	private setSlashOpen(open: boolean): void {
		this.slashOpen = open;
		if (open) {
			document.addEventListener('scroll', this.onScroll, true);
		} else {
			document.removeEventListener('scroll', this.onScroll, true);
		}
	}

	private readonly onScroll = (): void => {
		this.slashOverlay?.overlayRef?.updatePosition();
	};
}
